import React, { useContext, useEffect, useState } from 'react';
import { AB_TEST_RUNNING_MESSAGE, APPLETS, SELECT_APPLET } from '../../utils/Constants';
import { mapAppletToWidget } from '../../utils/ABHelper';
import { getLiveApplets, getAllApplets } from '../../services/ABServices';
import { AppContext } from 'Context/AppContext';
import { TAppletInfo, TAppletResponse, TPayload, TUpdateTest } from '../../types/ab';
import { ABContext } from '../../contexts/ABContext';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';

const AppletsSection = () => {
    const appContext = useContext(AppContext);
    const { setDefaultModels, setModels, appletInfo, setAppletInfo, setSelectedApplet, setUpdateTest, selectedApplet, setPayload, liveApplets, setLiveApplets } = useContext(ABContext);
    const [applets, setApplets] = useState<TAppletResponse[]>([]);

    useEffect(() => {
        if (appContext?.channelKey) {
            getApplets();
        }
    }, [appContext?.channelKey]);

    useEffect(() => {
        appendApplet(appletInfo);
    }, [appletInfo]);

    const handleSelectChange = (value: string | number) => {
        const selectedId = String(value);
        const selectedApplet = applets.find(applet => applet.applet_id === selectedId);
        if (liveApplets?.length === 0) fetchLiveApplets()
        if (selectedApplet) {
            const { applet_id, applet_name, applet } = selectedApplet;
            setAppletInfo((prev: TAppletInfo) => ({
                ...prev,
                applet: applet,
                applet_name: applet_name,
                applet_id: applet_id,
            }))
            setPayload((prevPayload: TPayload) => {

                const updatedPayload = {
                    ...prevPayload, applets: [{
                        applet: applet,
                        applet_name: applet_name,
                        applet_id: applet_id,
                    }]
                };
                return updatedPayload;
            });
            checkAppletInUse(selectedId)
        } else { setModels([]) }
        setSelectedApplet(selectedId);
    };

    const fetchLiveApplets = async () => {
        if (!appContext?.channelKey) return
        try {
            const liveAppletsResponse = await getLiveApplets(appContext?.channelKey)
            setLiveApplets(liveAppletsResponse?.data?.ab_test_applet || []);
            setDefaultModels(liveAppletsResponse?.data?.default_models || {})
            return liveAppletsResponse?.data?.ab_test_applet;
        } catch (error) {
            setLiveApplets([]);
            setDefaultModels({});
            toast.error('Failed to fetch live applets');
        }
    }

    const checkAppletInUse = async (appletId: string) => {
        let appletList = liveApplets;
        if (!appletList?.length) {
            const freshAppletList = await fetchLiveApplets();
            if (freshAppletList?.length) {
                appletList = freshAppletList;
            }
        }

        if (appletList?.length > 0 && appletList?.includes(appletId)) {
            toast.warning(AB_TEST_RUNNING_MESSAGE);
        }
    };

    const getApplets = async () => {
        try {
            if (!appContext?.channelKey) return
            const response = await getAllApplets(appContext?.channelKey ?? '');
            if (response?.done) {
                setApplets(response?.data ?? []);
            }
        } catch (error) {
            console.log("getApplets ~ error:", error)
        }
    }

    const appendApplet = (newApplet: TAppletInfo) => {
        setUpdateTest((prevUpdateTest: TUpdateTest) => ({
            ...prevUpdateTest,
            applets: [...prevUpdateTest.applets, newApplet],
        }));
    };

    /*
      The empty option is kept, not folded into `placeholder`.

      `handleSelectChange` has an `else` branch — no matching applet means
      `setModels([])` — and picking "Select Applet ..." is the only way to reach it.
      A placeholder alone would delete the ability to clear a chosen applet, the same
      trap the model picker had in § 137.
    */
    const appletOptions = [
        { value: '', label: SELECT_APPLET },
        ...applets.map(applet => ({
            value: applet.applet_id,
            label: mapAppletToWidget(applet.applet_name),
        })),
    ];

    return (
        <div className='tw-my-3 tw-pb-6 tw-border-b tw-border-edge'>
            <div className="tw-w-[20%]">
                <Dropdown
                    label={APPLETS}
                    labelSize="sm"
                    id="applet"
                    data-testid="detail-appletssection-select-1"
                    options={appletOptions}
                    value={selectedApplet || ''}
                    onChange={handleSelectChange}
                    placeholder={SELECT_APPLET}
                />
            </div>
        </div>
    );
}

export default AppletsSection;